import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { ElasticFormModal, Fetching, InfiniteScroll, ListFilter, ListRow, NoResults, Search, Switch, WithSelectable, WithTooltip } from 'basic-components';
import { Exit, Refresh } from 'basic-components/icons';
import { DataLakeReadableConsts } from 'dashboard-services/api/dataLake';
import Query from 'dashboard-services/api/Query';
import { CommonService } from 'dashboard-services/services';
import TimeService from 'dashboard-services/services/TimeService';
import { DropdownService, Fetching as FetchingPrimary, globalActions } from 'primary-components';

import getApiConfig from 'api/ApiConfig';
import { filesDatalakeQueries } from 'queries/modules/datalake';
import properties from 'resources/constants/properties.json';
import { sendEvent } from 'services/GAService';

import ProviderSelector from 'primary/ProviderSelector';

import FilterCarousel from 'primary/FilterCarousel';
import ModalTitle from './ModalTitle';

import { useQueryClient } from '@tanstack/react-query';
import { DashboardLinkFactory } from 'ng-common-features/urlBuilder';
import { When } from 'react-if';

import classNames from 'classnames';
import '../../Modal.scss';
import './Modal.scss';

const wildcardQuery = query => `name.keyword=*${query}*`
const EMPTY_ARRAY = [];
const Modal = ({ optionalText = undefined, closeModal, onSubmit, defaultValue = "", fileExtensions = EMPTY_ARRAY, fileType: defaultFileType = undefined, withVersion = true, withProvider = false }) => {
  const dispatch = useDispatch(),
        definedTime = useSelector(state => state.myAccountState.settingsState.definedTime),
        [value, setValue] = useState(defaultValue),
        [query, setQuery] = useState(""),
        [groupName, setGroupName] = useState(),
        [provider, setProvider] = useState(),
        [fileType, setFileType] = useState(defaultFileType),
        hasFileTypeFilter = fileType !== undefined && fileType !== null && fileType !== "",
        fileTypeOption = useMemo(() => {
          const option = DataLakeReadableConsts.FILE_TYPES.find(({ value }) => value === fileType)
          return option ? { label: option.label, value: option.value } : undefined
        }, [fileType]),
        [activeFileExtension, setActiveFileExtension] = useState(fileExtensions[0]),
        activeFilterCount = Number(Boolean(groupName)) + Number(Boolean(fileType)) + Number(Boolean(fileExtensions.length > 1 && activeFileExtension !== undefined)),
        clearAllFilters = useCallback(() => {
          setGroupName(undefined)
          setFileType(undefined)
          setActiveFileExtension(undefined)
        }, []),
        [showVersions, setShowVersions] = useState(false),
        
        onSubmitLocal = useCallback(() => {
          onSubmit(value ? Object.assign({}, value, { name: value?.fileName }) : value, !showVersions)
          closeModal()
        }, [closeModal, onSubmit, showVersions, value]),
        
        submitProps = useMemo(() => ({
          disabled: value === undefined || value === "" || value === " ",
          type: "button",
          onClick: onSubmitLocal,
          noDebounce: true
        }), [onSubmitLocal, value]),
        formProps = useMemo(() => ({
          noDebounce: true
        }), []),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        shouldBeLimited = useCallback(() => dispatch(globalActions.shouldTheApiBeLimited(provider)), [dispatch, provider]),
        queryClient = useQueryClient(),
        [groupQuery, setGroupQuery] = useState(),
        { fetchNextPage: fetchMoreGroups, isFetching: isFetchingGroups, isFetchingNextPage: isFetchingNextPageGroups, data: groups = EMPTY_ARRAY, hasNextPage: hasNextPageGroups } = DropdownService.useQueryGroups({
          getApiConfig: getApiConfigLocal,
          input: groupQuery,
          provider,
          queryClient
        }),
        finalQuery = useMemo(() => {
          let localQuery = String(query).includes("=") ? query : wildcardQuery(query)
          localQuery += activeFileExtension ? `.${activeFileExtension}` : ""
          if(groupName !== "" && groupName !== undefined) {
            localQuery += `&groupName.keyword=${groupName}`
          }
          if(hasFileTypeFilter) {
            localQuery += `&type=${fileType}`
          }
          if(!showVersions) {
            localQuery += `&latest=true`
          }
          return localQuery;
        }, [activeFileExtension, fileType, groupName, hasFileTypeFilter, query, showVersions]),
        onSelect = useCallback(response => (response?.pages || []).flatMap(p => p.items || []).map(item => Object.assign({}, item, { name: item.fileName, id: item.fid, provider })), [provider]),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(getApiConfigLocal())
          .withFetching(false)
          .withHeader("Target", provider, true)
        , [getApiConfigLocal, provider]),
        { data: files, fetchNextPage: onScroll, hasNextPage, isPending, isFetchingNextPage, isRefetching, refetch } = filesDatalakeQueries.useSearchFiles({ 
          query: finalQuery, 
          provider, 
          withStatus: false,
          getQueryBuilder, 
          shouldBeLimited,
          onSelect
        }),
        getUrl = useCallback(fileId => DashboardLinkFactory.dataLake.file({ fileId, provider }), [provider]),
        onProviderChange = useCallback((_, { value }) => setProvider(value), []),
        preventFilterSubmit = useCallback(e => e.preventDefault(), []),
        scrollContRef = useRef()

  useEffect(() => {
    const event = {
      category: "view_item",
      action: "file_picker",
      label: "picker",
    };
    dispatch(sendEvent(event))
  }, [dispatch])

  return (
    <ElasticFormModal
        className="ng-common-picker__modal ng-file-picker__modal"
        formProps={formProps}
        onClose={closeModal}
        onSubmit={onSubmitLocal}
        optionalText={optionalText || "File picker"}
        submitProps={submitProps}
        submitText="Select"
        title={<ModalTitle fileExtension={activeFileExtension}/>}
    >
      <div className="ng-file-picker__modal__top">
        <When condition={properties?.["sharing_enabled"] === "true" && withProvider}>
          {() => (
            <ProviderSelector
                externalProvider={provider}
                onProviderChange={onProviderChange}
                shouldUseSearchParams={false}
            />
          )}
        </When>
        <div className="ng-file-picker__modal__top__search">
          <Search 
              onSearch={setQuery}
              size="large"
              variant="outline"
          />
        </div>
        <FilterCarousel
            activeFilterCount={activeFilterCount}
            className="ng-file-picker__modal__filters"
            onClearAll={clearAllFilters}
            onClickCapture={preventFilterSubmit}
        >
          <ListFilter
              defaultValue={groupName}
              fetchNextPage={fetchMoreGroups}
              hasNextPage={hasNextPageGroups}
              isFetching={isFetchingGroups || isFetchingNextPageGroups}
              label="Group"
              name="groupName"
              onChange={setGroupName}
              onInputChange={setGroupQuery}
              options={groups}
          />
          <When condition={fileExtensions.length > 1}>
            {() => (
              <ListFilter
                  defaultValue={activeFileExtension}
                  label="Extension"
                  name="fileExtension"
                  onChange={setActiveFileExtension}
                  options={fileExtensions}
              />
            )}
          </When>
          <ListFilter
              defaultValue={fileTypeOption}
              label="Type"
              name="fileType"
              onChange={setFileType}
              options={DataLakeReadableConsts.FILE_TYPES}
          />
        </FilterCarousel>
      </div>
      <When condition={withVersion}>
        {() => (
          <div className="ng-common-picker__modal__version-swap">
            <div className="ng-common-picker__modal__version-swap__label">
              Versions
            </div>
            <Switch
                on={showVersions}
                onChange={setShowVersions}
            />
          </div>
        )}
      </When>
      <div className={classNames(
        "ng-common-picker__modal__headers",
        { "ng-common-picker__modal__headers--no-file-type": hasFileTypeFilter }
      )}>
        <div className="ng-common-picker__modal__headers__cell">
          Name
        </div>
        <div className="ng-common-picker__modal__headers__cell">
          Group
        </div>
        <When condition={!hasFileTypeFilter}>
          {() => (
            <div className="ng-common-picker__modal__headers__cell">
              Type
            </div>
          )}
        </When>
        <div className="ng-common-picker__modal__headers__cell">
          Created on
        </div>
        <WithTooltip
            data-place="left"
            short
            text={isRefetching || isPending || isFetchingNextPage ? "Data is refreshing" : "Refresh data"}
        >
          <div 
              className="ng-common-picker__modal__headers__cell"
              onClick={isRefetching || isPending || isFetchingNextPage ? undefined : refetch}
          >
            {isRefetching || isPending || isFetchingNextPage ? (
              <Fetching 
                  loading={isFetchingNextPage || isPending || isRefetching}
                  size={12}
              />
            ) : (
              <Refresh
                  color="blue-bright"
                  height={16}
                  width={16}
              />
            )}
          </div>
        </WithTooltip>
      </div>
      <div 
          className="ng-common-picker__modal__content"
          ref={scrollContRef}
      >
        <InfiniteScroll
            className="ng-common-picker__modal__content__scroll"
            hasMore={hasNextPage}
            initialLoad={false}
            loadMore={onScroll}
            threshold={400}
            useWindow={false}
        >
          {(files || []).map((file, index) => (
            <WithSelectable
                index={index}
                key={`File${index}${showVersions}`}
                selected={value?.name === file.name && file.groupName === value?.groupName && file.id === value?.id}
                setSelected={() => setValue(file)}
            >
              <ListRow
                  className={classNames(
                    "ng-common-picker__modal__content__scroll__row",
                    { "ng-common-picker__modal__content__scroll__row--no-file-type": hasFileTypeFilter }
                  )}
                  contentProps={{
                    onDoubleClick: () => {
                      setValue(file)
                      onSubmitLocal()
                      closeModal()
                    }
                  }}
                  onClick={() => setValue(file)}
                  scrollContRef={scrollContRef.current}
              >
                <div className="ng-common-picker__modal__content__scroll__row__cell">
                  {file.name}
                </div>
                <div className="ng-common-picker__modal__content__scroll__row__cell">
                  {file.groupName}
                </div>
                <When condition={!hasFileTypeFilter}>
                  {() => (
                    <div className="ng-common-picker__modal__content__scroll__row__cell">
                      {DataLakeReadableConsts.FILE_TYPES.find(f => f.value === file.fileType)?.label || file.fileType}
                    </div>
                  )}
                </When>
                <div className="ng-common-picker__modal__content__scroll__row__cell">
                  {file.arrivalTime ? TimeService.getUserFormattedDateTime(file.arrivalTime, definedTime) : ""}
                </div>
                <WithTooltip
                    data-place="top"
                    short
                    text="Open file"
                >
                  <div 
                      className="ng-common-picker__modal__exit"
                      onClick={() => CommonService.openInNewTab(getUrl(file.id))}
                  >
                    <Exit
                        color="blue-bright"
                        height={16}
                        width={16}
                    />
                  </div>
                </WithTooltip>
              </ListRow>
            </WithSelectable>
          ))}
        </InfiniteScroll>
        <FetchingPrimary isFetching={isPending}/>
        <When condition={(files || []).length === 0 && !(isFetchingNextPage || isPending || isRefetching)}>
          {() => (
            <NoResults isModal/>
          )}
        </When>
      </div>
    </ElasticFormModal>
    
  )
}

Modal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  defaultValue: PropTypes.string,
  fileExtensions: PropTypes.arrayOf(PropTypes.string),
  fileType: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  optionalText: PropTypes.string,
  withProvider: PropTypes.bool,
  withVersion: PropTypes.bool,
}

export default memo(Modal);
